/**
 * Add to cart button handler
 */

import { validateGridQuantities } from "../utils/validation.js";
import { updateCart, fetchCart } from "../services/cartService.js";
import { showCustomPopup } from "../components/popup.js";
import { setBaselinesFromSnapshot } from "../components/dirtyTracking.js";
import { triggerCartSparkle } from "../components/cartSparkle.js";
import { pushGridDiff } from "../services/cartSync/syncService.js";

const CART_MODAL_AUTO_CLOSE_MS = 3000;

/**
 * Show the "Cart Updated" confirmation modal.
 * Has "View Cart" and "Continue" buttons plus a 3-second auto-close countdown.
 */
function showCartUpdatedModal(): void {
  $(".cart-updated-overlay").remove();

  const html = `
    <div class="custom-popup-overlay cart-updated-overlay">
      <div class="custom-popup cart-updated-modal">
        <div class="custom-popup__message">Cart Updated!</div>
        <div class="cart-updated-modal__actions">
          <button class="cart-updated-modal__view-cart">View Cart</button>
          <button class="cart-updated-modal__continue">Continue</button>
        </div>
        <div class="cart-updated-modal__progress-bar">
          <div class="cart-updated-modal__progress-fill"></div>
        </div>
      </div>
    </div>
  `;

  $("body").append(html);

  const $overlay = $(".cart-updated-overlay");

  // Fade in
  setTimeout(() => $overlay.addClass("show"), 10);

  function closeModal(): void {
    clearTimeout(autoCloseTimer);
    $overlay.removeClass("show");
    setTimeout(() => $overlay.remove(), 200);
  }

  // Start progress-bar drain after a tick so the transition fires
  const $fill = $overlay.find(".cart-updated-modal__progress-fill");
  setTimeout(() => {
    $fill.css({
      transition: `transform ${CART_MODAL_AUTO_CLOSE_MS}ms linear`,
      transform: "scaleX(0)",
    });
  }, 20);

  const autoCloseTimer = setTimeout(closeModal, CART_MODAL_AUTO_CLOSE_MS);

  $overlay.find(".cart-updated-modal__view-cart").on("click", () => {
    clearTimeout(autoCloseTimer);
    window.location.href = "/cart";
  });

  $overlay.find(".cart-updated-modal__continue").on("click", closeModal);

  // Click outside closes
  $overlay.on("click", function (e: JQuery.ClickEvent) {
    if ($(e.target).hasClass("cart-updated-overlay")) {
      closeModal();
    }
  });
}

/**
 * Refresh the header cart-count badge from the current server cart, and fire
 * the sparkle burst to give the user visual confirmation the cart changed.
 * Swallows failures — a stale badge shouldn't mask a successful cart update.
 */
async function refreshHeaderCartCount(): Promise<void> {
  try {
    const cart = await fetchCart();
    triggerCartSparkle(cart.item_count);
  } catch (error) {
    console.warn("Failed to refresh header cart count", error);
  }
}

/**
 * Run the cart update: diff the grid against the server cart, POST updates
 * and adds, then re-baseline the dirty tracker. Optionally show the success
 * modal on completion. Throws if any underlying fetch fails.
 */
export async function performCartUpdate(options: {
  showSuccessPopup: boolean;
}): Promise<void> {
  const { snapshot, diff } = await updateCart();
  setBaselinesFromSnapshot(snapshot);
  // Cart sync mirrors the exact diff we just submitted. Fire-and-forget: a
  // sync failure must never make a successful cart update look failed.
  void pushGridDiff(diff);
  await refreshHeaderCartCount();
  if (options.showSuccessPopup) {
    showCartUpdatedModal();
  }
}

/**
 * Shared "failed to update cart" popup — reload prompt, same UX everywhere.
 */
export function showCartUpdateErrorPopup(): void {
  showCustomPopup(
    "Failed to update cart. Please try again or contact support.",
    0,
    "OK",
    () => window.location.reload(),
  );
}

/**
 * Initialize add to cart button handler
 */
export function initAddToCartHandler(): void {
  // Delegated binding so buttons in lazy-loaded rows also trigger the handler.
  // Direct binding on `.inventory_submit` only catches elements present at
  // init time; per-row Update Cart buttons in lazy-loaded colors would never
  // fire.
  $(document).on("click", ".inventory_submit", async function (e: JQuery.ClickEvent) {
    e.preventDefault();

    const $btn = $(this);

    $btn.prop("disabled", true).addClass("loading");
    $btn.prepend('<div class="spinnerX"></div>');

    try {
      const validation = validateGridQuantities();
      if (!validation.valid) {
        showCustomPopup(
          "Please correct the following errors:<br>" +
            validation.errors.join("<br>"),
          0,
        );
        return;
      }

      await performCartUpdate({ showSuccessPopup: true });
    } catch (error) {
      console.error("Cart update failed:", error);
      showCartUpdateErrorPopup();
    } finally {
      $btn.prop("disabled", false).removeClass("loading");
      $btn.find(".spinnerX").remove();
    }
  });
}
